import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ProductFormValues,
  IDropdownProduct,
  IEntityState,
  IProduct,
  ProductsState,
  IProductResponse,
} from "../../../types/product";
import { mapProductResponseToForm } from "../../../mappers/product/productMapper";

/**
 * 🧾 Estado inicial para el formulario de producto.
 */
export const initialStateForm: ProductFormValues = {
  // =========================================================
  // 🆔 IDENTIFICACIÓN
  // =========================================================
  id: null,

  // =========================================================
  // 🧩 PRINCIPALES
  // =========================================================
  code: null,
  name: null,
  description: null,

  // =========================================================
  // 💰 COSTOS / INVENTARIO
  // =========================================================
  price: null,
  cost: null,
  stock: null,
  status: true,
  minimumSalePrice: null,
  tipEligible: false,
  appliesTourismTax: false,
  stockControlEnabled: false,
  appliesDiscount: true,

  // =========================================================
  // 📦 INVENTARIO INICIAL
  // =========================================================
  createInitialStock: false,

  initialStockBranchId: null,

  minStock: null,

  reorderPoint: null,

  // =========================================================
  // 🔗 RELACIONES
  // =========================================================
  categoryId: null,
  unitId: null,
  itemTypeId: null,
  itemTypeCode: null,

  // =========================================================
  // 🧾 IDENTIFICADORES EXTERNOS
  // =========================================================
  barcode: null,
  sku: null,
  manufacturerCode: null,
  searchCode: null,

  // =========================================================
  // 🏷️ CLASIFICACIÓN
  // =========================================================
  brandId: null,
  subCategoryId: null,

  // =========================================================
  // 🧬 TRAZABILIDAD
  // =========================================================
  lotNumber: null,
  batch: null,
  expirationDate: null,

  // =========================================================
  // 🧵 ATRIBUTOS / VARIANTES
  // =========================================================
  colorId: null,
  colorName: null,
  variant: null,
  sizeId: null,
  sizeName: null,

  // =========================================================
  // 🧨 CONSUMIBLE
  // =========================================================
  consumable: false,

  // =========================================================
  // 📦 DIMENSIONES
  // =========================================================
  weight: null,
  height: null,
  width: null,
  length: null,

  // =========================================================
  // ❄️ CONDICIONES ESPECIALES
  // =========================================================
  humiditySensitive: false,
  coldChain: false,

  // =========================================================
  // 🏷️ UBICACIÓN FÍSICA (flat)
  // =========================================================
  storageLocationId: null,
  storageAisle: null,
  storageRow: null,
  storageShelf: null,
  storageBin: null,

  // =========================================================
  // 🖼️ IMAGEN
  // =========================================================
  imagePath: undefined,
  imageUrl: undefined,
  file: undefined,
};


/**
 * 🌐 Estado global del slice de productos.
 */
export const initialState: ProductsState = {
  form: initialStateForm, // nuevo
  products: [],
  productsDropdown: [],
  paginator: null,
  pageSize: 5,
  isCreateProductModalOpen: false,
  isEditProductModalOpen: false,
  isViewProductModalOpen: false,
  isSubmitting: false,
  formErrors: {}
};

export const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    // 📦 Productos paginados
    loadingProducts: (state, action: PayloadAction<{ content: IProduct[] }>) => {
      state.products = action.payload.content;
      state.paginator = action.payload;
    },

    // 📋 Productos para dropdown general
    loadingProductsDropdown: (state, action: PayloadAction<IDropdownProduct[]>) => {
      state.productsDropdown = action.payload;
    },

    // 🔄 Cambiar estado activo/inactivo
    toggleProductState: (state, action: PayloadAction<IEntityState>) => {
      const { id, status } = action.payload;
      state.products = state.products.map((product) =>
        product.id === id ? { ...product, status } : product
      );
    },

    // ➕ Agregar nuevo producto
    addProduct: (state, action: PayloadAction<IProduct>) => {
      state.products = [action.payload, ...state.products];
    },

    // 🛠️ Actualizar producto
    updateProduct: (state, action: PayloadAction<IProduct>) => {
      state.products = state.products.map((product) =>
        product.id === action.payload.id ? { ...product, ...action.payload } : product
      );
    },

    // 📄 Cambiar tamaño de página
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },

    // 📑 Establecer detalles del producto
    setProductDetail: (state, action: PayloadAction<IProductResponse>) => {
      state.form = mapProductResponseToForm(action.payload);
    },


    // 🆕 Actualiza un campo del formulario
    updateProductField: (
      state,
      action: PayloadAction<{ field: keyof ProductFormValues; value: any }>
    ) => {
      const { field, value } = action.payload;
      state.form = {
        ...state.form,
        [field]: value,
      };
    },

    // 🧹 Limpia el formulario
    resetProductForm: (state) => {
      state.form = initialStateForm;
    },

    resetFormOnItemTypeChange: (
      state,
      action: PayloadAction<{
        itemTypeId: number;
        itemTypeCode: number;
        code: string | null;
      }>
    ) => {
      state.form = {
        ...initialStateForm,
        itemTypeId: action.payload.itemTypeId,
        itemTypeCode: action.payload.itemTypeCode,
        code: action.payload.code,
      };
    },

    setIsCreateProductModalOpen(state, action: PayloadAction<boolean>) {
      state.isCreateProductModalOpen = action.payload;
    },
    setIsEditProductModalOpen(state, action: PayloadAction<boolean>) {
      state.isEditProductModalOpen = action.payload;
    },
    setIsViewProductModalOpen(state, action: PayloadAction<boolean>) {
      state.isViewProductModalOpen = action.payload;
    },

    setIsSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },

    // 🐞 Guarda errores de validación del backend
    setProductErrors: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.formErrors = action.payload;
    },

    // 🧽 Limpia errores del formulario
    clearProductErrors: (state) => {
      state.formErrors = {};
    },

  },
});

// ✅ Exportar acciones
export const {
  loadingProducts,
  loadingProductsDropdown,
  toggleProductState,
  setPageSize,
  addProduct,
  updateProduct,
  setProductDetail,
  updateProductField,
  resetProductForm,
  resetFormOnItemTypeChange,
  setIsCreateProductModalOpen,
  setIsEditProductModalOpen,
  setIsViewProductModalOpen,
  setIsSubmitting,
  setProductErrors,
  clearProductErrors,
} = productSlice.actions;

// ✅ Exportar reducer
export default productSlice.reducer;
